// src/pages/Labs.tsx
import { Link } from "react-router-dom";
import LayoutWrapper from "../../components/layout/LayoutWrapper";
import Section from "../../components/ui/Section";

const Labs = () => {
  return (
    <div className="bg-bg">
      {/* Hero */}
      <section className="py-14 sm:py-16 border-b border-line">
        <LayoutWrapper>
          <div className="grid lg:grid-cols-[1.4fr,1fr] gap-10 items-start">
            <div>
              <p className="kicker mb-4">NOVTIA Labs</p>
              <h1 className="font-display text-2xl sm:text-3xl md:text-4xl font-semibold text-ink mb-4">
                The research and engineering engine behind{" "}
                <span className="text-signal">NOVTIA&apos;s future capabilities.</span>
              </h1>
              <p className="text-sm sm:text-base text-muted max-w-xl mb-4">
                Labs develops the tools, patterns, and frameworks that extend
                the NOVTIA Sovereign Core. The work can become reusable
                capability in client systems, future products, or new ways to
                operate AI with policy awareness, observability, and clear
                accountability.
              </p>
              <p className="text-sm text-muted max-w-xl mb-6">
                Everything in NOVTIA Labs is grounded in real constraints from
                utilities, finance, and healthcare — not just theoretical
                architectures.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link
                  to="/contact"
                  className="inline-flex items-center bg-signal px-5 py-2 text-sm font-semibold text-on-signal hover:bg-signal-strong transition-colors"
                >
                  Explore collaboration
                </Link>
                <Link
                  to="/insights"
                  className="inline-flex items-center border border-line-strong px-5 py-2 text-sm text-ink hover:border-signal hover:text-signal transition-colors"
                >
                  Read our thinking
                </Link>
              </div>
            </div>

            {/* Side panel: philosophy */}
            <div className="surface-card p-5 sm:p-6 text-sm">
              <p className="kicker mb-3">Labs philosophy</p>
              <ul className="space-y-3">
                <li>
                  <span className="font-semibold text-ink">
                    Product, not slides:
                  </span>{" "}
                  <span className="text-muted">
                    we build components that can be wired into real stacks,
                    not just diagrams.
                  </span>
                </li>
                <li>
                  <span className="font-semibold text-ink">
                    Governance-first:
                  </span>{" "}
                  <span className="text-muted">
                    safety, observability, and governance are primary design
                    inputs, not last-minute requirements.
                  </span>
                </li>
                <li>
                  <span className="font-semibold text-ink">
                    Builder-first:
                  </span>{" "}
                  <span className="text-muted">
                    we design for the engineers, data scientists, and
                    architects who will live with these systems.
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </LayoutWrapper>
      </section>

      {/* AIRG ORIGIN */}
      <section className="border-b border-line">
        <Section
          eyebrow="From Labs to product"
          title="AIRG began as a practical answer to a real governance gap."
          description="AIRG is NOVTIA’s first product to graduate from the same research and engineering discipline that drives Labs."
        >
          <div className="grid md:grid-cols-[1.2fr,0.8fr] gap-8 items-start text-sm">
            <div className="space-y-3">
              <p className="text-ink">
                Labs kept meeting the same wall: teams could describe exactly
                what an agent should never be allowed to do, but had nowhere to
                enforce it at the moment the agent acted.
              </p>
              <p className="text-muted">
                AIRG began as the enforcement point for that gap — an
                evaluation step inside the execution path rather than a review
                of logs afterwards. It has since moved out of Labs into a live
                design-partner programme.
              </p>
              <Link
                to="/products/airg"
                className="inline-flex text-sm font-semibold text-signal hover:underline"
              >
                Explore AIRG →
              </Link>
            </div>
            <div className="surface-card p-5">
              <p className="kicker mb-3">What graduated</p>
              <ul className="space-y-1.5 text-muted">
                <li>• Policy evaluation inside the execution path.</li>
                <li>• Signed, chained records of every decision.</li>
                <li>• Human review routing for high-risk actions.</li>
                <li>• Budget-aware limits on agent activity.</li>
              </ul>
            </div>
          </div>
        </Section>
      </section>

      {/* Other Labs initiatives */}
      <section className="border-b border-line">
        <Section
          eyebrow="Other initiatives"
          title="Building blocks for trusted AI systems."
          description="Beyond AIRG, NOVTIA Labs works on composable components, blueprints, and earlier-stage concepts that strengthen the Sovereign Core and may inform future products or client solutions."
        >
          <div className="surface-card grid md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-line">
            {[
              {
                title: "Observability Fabric",
                body: "A pattern for wiring traces, metrics, evaluations, and safety signals across LLMs, agents, and classical ML — using open standards wherever possible.",
                bullets: [
                  "End-to-end request tracing and correlation.",
                  "LLM and agent evaluation harnesses.",
                  "Safety trigger and anomaly dashboards.",
                ],
              },
              {
                title: "Governed RAG Blueprints",
                body: "RAG patterns for legal, healthcare, and operational use-cases, with explicit control over retrieval, grounding, and citation.",
                bullets: [
                  "Domain-specific retrieval strategies.",
                  "Guardrails for hallucinations and scope creep.",
                  "Patterns for citing and surfacing underlying sources.",
                ],
              },
              {
                title: "Policy & Playbook Library",
                body: "Templates and examples for internal AI policies, runbooks, and decision records — built from real engagements, not abstract specs.",
                bullets: [
                  "Policy templates for internal and external AI.",
                  "Incident and decision runbook patterns.",
                  "Examples tuned for regulated environments.",
                ],
              },
            ].map((item) => (
              <div key={item.title} className="p-6">
                <h3 className="font-display text-base font-semibold text-ink mb-2">
                  {item.title}
                </h3>
                <p className="text-sm text-muted mb-3">{item.body}</p>
                <ul className="space-y-1.5 text-sm text-muted">
                  {item.bullets.map((b) => (
                    <li key={b} className="flex gap-2.5">
                      <span className="mt-1.5 h-1 w-1 flex-none bg-line-strong" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </Section>
      </section>

      {/* Collaboration CTA */}
      <section>
        <Section>
          <div className="surface-card p-6 sm:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div>
              <p className="kicker mb-3">Collaborate with NOVTIA Labs</p>
              <h2 className="font-display text-xl sm:text-2xl font-semibold text-ink mb-2">
                Have a high-stakes AI challenge that needs a governance-first
                approach?
              </h2>
              <p className="text-sm text-muted max-w-xl">
                Labs works with a small number of partners each year to
                co-design and test architectures before they become
                mainstream. If you&apos;re pushing the boundaries of AI in a
                regulated or sensitive context, we should talk.
              </p>
            </div>
            <Link
              to="/contact"
              className="inline-flex items-center bg-signal px-6 py-2.5 text-sm font-semibold text-on-signal hover:bg-signal-strong transition-colors flex-none"
            >
              Start a Labs conversation
            </Link>
          </div>
        </Section>
      </section>
    </div>
  );
};

export default Labs;
